import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { AuthStoreStrategy } from '../../feature-seatmap/strategies/authorization/auth-store.strategy';
import { UrlFlagsService } from '../../feature-seatmap/services/url-flags/url-flags.service';
import { ViewportService } from '../../feature-seatmap/services/viewport/viewport.service';
import { EnrichedRecentProject } from '../../feature-seatmap/services/recent-projects/recent-project.interface';

const GETTING_STARTED_COLLAPSED_KEY = 'ocp.getting-started.collapsed';

function gettingStartedCollapsedKey(userSub: string): string {
  return `${GETTING_STARTED_COLLAPSED_KEY}.${userSub}`;
}

function readGettingStartedExpanded(userSub: string): boolean {
  try {
    return localStorage.getItem(gettingStartedCollapsedKey(userSub)) !== 'true';
  } catch {
    return true;
  }
}

@Injectable()
export class RecentProjectsStoreService {
  private readonly urlFlags = inject(UrlFlagsService);
  private readonly viewport = inject(ViewportService);
  private readonly authStore = inject(AuthStoreStrategy);

  // State properties
  private readonly _recentProjects = signal<EnrichedRecentProject[]>([]);
  readonly recentProjects = this._recentProjects.asReadonly();

  private readonly _loading = signal(true);
  readonly loading = this._loading.asReadonly();

  private readonly _getStartedExpanded = linkedSignal(() =>
    readGettingStartedExpanded(this.authStore.userSub())
  );
  readonly getStartedExpanded = this._getStartedExpanded.asReadonly();

  private readonly _tasksExpanded = signal(false);
  readonly tasksExpanded = this._tasksExpanded.asReadonly();

  /** Whether the app is running in embedded mode */
  readonly isEmbedded = this.urlFlags.embedded;

  /** Whether the viewport matches mobile breakpoint */
  readonly isMobile = this.viewport.isMobile;

  // Computed properties
  readonly hasProjects = computed(() => this._recentProjects().length > 0);

  /** Whether to show the embedded mobile empty state placeholder */
  readonly showEmbeddedMobileEmptyState = computed(
    () => this.isEmbedded() && this.isMobile() && !this.hasProjects() && !this._loading()
  );

  /** Sets the recent projects */
  setRecentProjects(projects: EnrichedRecentProject[]): void {
    this._recentProjects.set(projects);
  }

  /** Sets the loading state */
  setLoading(value: boolean): void {
    this._loading.set(value);
  }

  /** Toggles the get started section expansion and persists collapsed state */
  toggleGetStartedExpanded(): void {
    this._getStartedExpanded.update(v => !v);
    try {
      const key = gettingStartedCollapsedKey(this.authStore.userSub());
      if (this._getStartedExpanded()) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, 'true');
      }
    } catch {
      // storage unavailable (e.g. blocked in embedded/iframe context)
    }
  }

  /** Toggles the tasks section expansion */
  toggleTasksExpanded(): void {
    this._tasksExpanded.update(v => !v);
  }

  /** Sets the tasks section expansion state */
  setTasksExpanded(value: boolean): void {
    this._tasksExpanded.set(value);
  }
}
